import * as signalR from '@microsoft/signalr'
import { tokenStorage } from './tokenStorage'

// The hubs are mapped on the User API host — see Program.cs, which maps /hub/notifications,
// /hub/chat, /hub/flow and /hub/diagnostics there and nowhere else. This pointed at
// coms.hadv.quest, which has no DNS record at all, so every hub connection from this app failed
// before it started.
const SIGNALR_URL = import.meta.env.VITE_SIGNALR_URL || 'https://api.hadv.quest'

class SignalRService {
  private chatConnection: signalR.HubConnection | null = null
  private notificationConnection: signalR.HubConnection | null = null
  private diagnosticsConnection: signalR.HubConnection | null = null

  // Chat Hub
  async startChatConnection(token: string) {
    if (this.chatConnection?.state === signalR.HubConnectionState.Connected) {
      return this.chatConnection
    }

    // Use Trace for most detailed SignalR logs, Debug for detailed, Information for normal
    const logLevel = import.meta.env.DEV ? signalR.LogLevel.Trace : signalR.LogLevel.Information

    this.chatConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${SIGNALR_URL}/hub/chat`, {
        // Read when the hub asks, not captured at connect. accessTokenFactory runs again on every
        // reconnect, and closing over the starting token means a reconnect presents a credential
        // that has since expired — survivable for a short chat, fatal for a session watched for an
        // hour. `token` stays as the fallback for the first connect.
        accessTokenFactory: () => tokenStorage.getAccessToken() || token,
      })
      .withAutomaticReconnect()
      .configureLogging(logLevel)
      .build()

    // Add connection state handlers for chat
    this.chatConnection.onreconnecting(error => {
      console.warn('⚠️ SignalR Chat reconnecting...', error)
    })

    this.chatConnection.onreconnected(connectionId => {
      console.log('✅ SignalR Chat reconnected. Connection ID:', connectionId)
    })

    this.chatConnection.onclose(error => {
      console.error('❌ SignalR Chat connection closed:', error)
    })

    try {
      await this.chatConnection.start()
      console.log('SignalR Chat Connected')
      return this.chatConnection
    } catch (err) {
      console.error('SignalR Chat Connection Error:', err)
      throw err
    }
  }

  async stopChatConnection() {
    if (this.chatConnection) {
      await this.chatConnection.stop()
      this.chatConnection = null
      console.log('SignalR Chat Disconnected')
    }
  }

  getChatConnection() {
    return this.chatConnection
  }

  /**
   * Diagnostics relay.
   *
   * Its own connection rather than a second use of the notification hub: it lives only while
   * somebody is watching a session, and stopping it must not take notifications down with it.
   */
  async startDiagnosticsConnection(token: string) {
    if (this.diagnosticsConnection?.state === signalR.HubConnectionState.Connected) {
      return this.diagnosticsConnection
    }

    this.diagnosticsConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${SIGNALR_URL}/hub/diagnostics`, {
        accessTokenFactory: () => tokenStorage.getAccessToken() || token,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build()

    await this.diagnosticsConnection.start()
    return this.diagnosticsConnection
  }

  async stopDiagnosticsConnection() {
    if (this.diagnosticsConnection) {
      await this.diagnosticsConnection.stop().catch(() => undefined)
      this.diagnosticsConnection = null
    }
  }

  getDiagnosticsConnection() {
    return this.diagnosticsConnection
  }

  // Notification Hub
  async startNotificationConnection(token: string) {
    if (this.notificationConnection?.state === signalR.HubConnectionState.Connected) {
      console.log('✅ SignalR Notifications already connected')
      return this.notificationConnection
    }

    console.log('🔌 Attempting to connect to Notification Hub...')
    console.log('📍 Hub URL:', `${SIGNALR_URL}/hub/notifications`)

    this.notificationConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${SIGNALR_URL}/hub/notifications`, {
        // Read when the hub asks, not captured at connect. accessTokenFactory runs again on every
        // reconnect, and closing over the starting token means a reconnect presents a credential
        // that has since expired — survivable for a short chat, fatal for a session watched for an
        // hour. `token` stays as the fallback for the first connect.
        accessTokenFactory: () => tokenStorage.getAccessToken() || token,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: retryContext => {
          if (retryContext.elapsedMilliseconds < 60000) {
            return Math.random() * 10000
          } else {
            return null
          }
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .build()

    // Add connection state handlers
    this.notificationConnection.onreconnecting(error => {
      console.warn('⚠️ SignalR Notifications reconnecting...', error)
    })

    this.notificationConnection.onreconnected(connectionId => {
      console.log('✅ SignalR Notifications reconnected. Connection ID:', connectionId)
    })

    this.notificationConnection.onclose(error => {
      console.error('❌ SignalR Notifications connection closed:', error)
    })

    try {
      await this.notificationConnection.start()
      console.log('✅ SignalR Notifications Connected')
      console.log('📡 Connection ID:', this.notificationConnection.connectionId)
      console.log('🔊 Connection State:', this.notificationConnection.state)
      return this.notificationConnection
    } catch (err) {
      console.error('❌ SignalR Notifications Connection Error:', err)
      throw err
    }
  }

  async stopNotificationConnection() {
    if (this.notificationConnection) {
      console.log('⚠️ Stopping notification connection...')
      await this.notificationConnection.stop()
      this.notificationConnection = null
      console.log('🔌 SignalR Notifications Disconnected')
    } else {
      console.log('ℹ️ No notification connection to stop')
    }
  }

  getNotificationConnection() {
    return this.notificationConnection
  }

  // Disconnect all
  async disconnectAll() {
    await this.stopChatConnection()
    await this.stopNotificationConnection()
  }
}

export const signalRService = new SignalRService()
